import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import {
  requestNotificationPermission,
  onForegroundMessage,
} from '../config/firebase';
import { register_fcm_url } from '../utils/constants';

/**
 * Hook to manage push notifications for the admin panel.
 * - Registers the service worker
 * - Requests notification permission
 * - Sends the FCM token to the backend
 * - Listens for foreground messages and shows toasts
 *
 * Usage: const { permissionStatus, enableNotifications } = useNotifications();
 */
const useNotifications = () => {
  const [permissionStatus, setPermissionStatus] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [fcmToken, setFcmToken] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const toast = useToast();
  const unsubRef = useRef(null);

  // Register the Firebase messaging service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    try {
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      );
      console.log('Service worker registered:', registration.scope);
      return registration;
    } catch (err) {
      console.error('SW registration failed:', err);
      return null;
    }
  }, []);

  // Send FCM token to the backend
  const sendTokenToBackend = useCallback(async (token) => {
    try {
      const device = `${navigator.userAgent.substring(0, 50)}`;
      await axios.post(register_fcm_url, { token, device });
      console.log('FCM token sent to backend');
    } catch (err) {
      console.error('Failed to send FCM token to backend:', err);
    }
  }, []);

  // Enable notifications (called by user action)
  const enableNotifications = useCallback(async () => {
    if (!('Notification' in window)) {
      toast({
        title: 'Notifications not supported',
        description: 'Your browser does not support push notifications.',
        status: 'warning',
        duration: 4000,
      });
      return false;
    }

    setIsRegistering(true);
    try {
      // 1. Register service worker
      await registerServiceWorker();

      // 2. Request permission and get token
      const token = await requestNotificationPermission();

      if (token) {
        setFcmToken(token);
        setPermissionStatus('granted');

        // 3. Send token to backend
        await sendTokenToBackend(token);

        // Store token locally to detect changes
        localStorage.setItem('darista_fcm_token', token);

        toast({
          title: '🔔 Notifications enabled!',
          description: "You'll receive alerts for new orders on this device.",
          status: 'success',
          duration: 4000,
        });
        return true;
      } else {
        setPermissionStatus(Notification.permission);
        if (Notification.permission === 'denied') {
          toast({
            title: 'Notifications blocked',
            description: 'Please enable notifications in your browser settings.',
            status: 'error',
            duration: 5000,
          });
        }
        return false;
      }
    } catch (err) {
      console.error('Enable notifications error:', err);
      toast({
        title: 'Notification setup failed',
        description: err.message,
        status: 'error',
        duration: 4000,
      });
      return false;
    } finally {
      setIsRegistering(false);
    }
  }, [registerServiceWorker, sendTokenToBackend, toast]);

  // Auto-setup on mount if permission was previously granted
  useEffect(() => {
    const init = async () => {
      if (typeof Notification === 'undefined') return;

      if (Notification.permission === 'granted') {
        const swReg = await registerServiceWorker();
        if (swReg) {
          const token = await requestNotificationPermission();
          if (token) {
            setFcmToken(token);
            setPermissionStatus('granted');

            // Re-register token if it changed
            const storedToken = localStorage.getItem('darista_fcm_token');
            if (storedToken !== token) {
              await sendTokenToBackend(token);
              localStorage.setItem('darista_fcm_token', token);
            }
          }
        }
      }
    };
    init();
  }, [registerServiceWorker, sendTokenToBackend]);

  // Listen for foreground messages
  useEffect(() => {
    if (permissionStatus !== 'granted' || !fcmToken) return;

    unsubRef.current = onForegroundMessage(({ title, body, data }) => {
      // Show a toast for foreground notifications
      toast({
        title: title || '🛒 New Order!',
        description: body || 'A new order has been placed.',
        status: 'info',
        duration: 8000,
        isClosable: true,
        position: 'top-right',
      });

      // Also show a native notification if the page is not focused
      if (document.hidden && Notification.permission === 'granted') {
        const notif = new Notification(title || '🛒 New Order!', {
          body: body || 'A new order has been placed.',
          icon: '/logo192.png',
          tag: data?.orderId ? `order-${data.orderId}` : undefined,
        });
        notif.onclick = () => {
          window.focus();
          if (data?.orderId) {
            window.location.href = `/orders/${data.orderId}`;
          }
        };
      }
    });

    return () => {
      if (typeof unsubRef.current === 'function') {
        unsubRef.current();
      }
    };
  }, [permissionStatus, fcmToken, toast]);

  return {
    permissionStatus,
    fcmToken,
    isRegistering,
    enableNotifications,
    isSupported: typeof Notification !== 'undefined' && 'serviceWorker' in navigator,
  };
};

export default useNotifications;
